import React, { useEffect ,useState} from "react";
import { useAssetContext } from "../../DataContext/AssetContext";
import ChartComponent from "../ConfigCharts/StackedBarLine";
import WindmillLoading from "../../Loading/Loading";
import ApiErrorMessage from "./ApiErrorMessage"; 

const VolumeDeltaSetup = () => {
  const {
VolumeDeltaAsset,
    VolumeDeltaMetrics,
    selectedResource,
    setSelectedResource,
    isDeltaLoading,
    setIsDeltaLoading, 
    selectedFrequency,
    selectedDateRange,
    selectedRevenue,
    useIntervalAggregation, 
    resourceNames,
    DeltaApiError,
    GroupByVolumeDeltaMetrics,
    Net_Mw_Data,
    setVolumeDeltaAsset,
    selectedAssetId
  } = useAssetContext();

  interface ChartData {
    time: string;
    [key: string]: number | string; 
  }

  useEffect(() => {
    setVolumeDeltaAsset(null);
  }, [selectedAssetId]);
  
  let activeMetrics: string[] = [];

  if (selectedRevenue === "market_type") {
    activeMetrics = GroupByVolumeDeltaMetrics;
  } else if (selectedRevenue === "product") {
    activeMetrics = GroupByVolumeDeltaMetrics;
  } else if (selectedRevenue === "award_type") {
    activeMetrics = GroupByVolumeDeltaMetrics;
  } else if (selectedRevenue === "resource") {
  activeMetrics = resourceNames.filter(name => name !== "ALL");

  // if (selectedResource === "ALL") {
  //   activeMetrics = ["asset_revenue"]; 
  // }
}
 else if (selectedRevenue === "settlement_charge_name") {
    activeMetrics = GroupByVolumeDeltaMetrics;
  } else if (selectedRevenue === "total") {
    activeMetrics = GroupByVolumeDeltaMetrics;
  } else {
    activeMetrics = Array.isArray(VolumeDeltaMetrics) ? VolumeDeltaMetrics : ["DAM", "FMM", "RTM"];
  }

  if (!activeMetrics.includes("METERED_OUTPUT")) {
    activeMetrics.push("METERED_OUTPUT");
  }
 
  
  interface Resource {
    RESOURCE_ID: string;
    RESOURCE_NAME: string;
    [metric: string]: any;
  }
  
  const getResources = (data: any): Resource[] => {
    if (data && typeof data === "object" && "RESOURCES" in data && Array.isArray(data.RESOURCES)) {
      return data.RESOURCES;
    }
    return [];
  };
  
  const volumeDeltaResources: Resource[] = getResources(VolumeDeltaAsset);
  const netMwResources: Resource[] = getResources(Net_Mw_Data);
  
  const combinedDataSource: Resource[] = [
    ...(Array.isArray(volumeDeltaResources) ? volumeDeltaResources : []),
    ...(Array.isArray(netMwResources) ? netMwResources : []),
    ...(typeof VolumeDeltaAsset === "object" ? [VolumeDeltaAsset] : []),
    ...(typeof Net_Mw_Data === "object" ? [Net_Mw_Data] : []),
  ];
  
  const cleanCombinedDataSource = (combinedDataSource ?? []).filter(Boolean);
  
  const allData =
    selectedResource === "ALL"
      ? [VolumeDeltaAsset, Net_Mw_Data]
      : cleanCombinedDataSource.filter(
          (resource: Resource) => resource.RESOURCE_NAME === selectedResource
        );
  
  const rawChartData = allData
    .flatMap((resource: Resource | null) =>
      resource && typeof resource === "object"
        ? Object.entries(resource)
            .filter(([key, values]) =>
              key !== "RESOURCE_ID" &&
              key !== "RESOURCE_NAME" &&
              typeof values === "object" &&
              values !== null
            )
            .flatMap(([metric, values]) =>
              values
                ? Object.entries(values).map(([time, value]) => ({
                    time: isNaN(new Date(time).getTime()) ? null : time,
                    RESOURCE_NAME: resource.RESOURCE_NAME || "Unknown",
                    [metric]: value ?? 0,
                  }))
                : []
            )
        : []
    )
    .filter((entry) => entry.time !== null);

    
  
  // Aggregation logic remains the same
  const groupedData: { [key: string]: ChartData } = {};
  
  rawChartData.forEach((entry) => {
    if (!entry || typeof entry !== "object" || !("time" in entry)) return;
    if (typeof entry.time === "string" && entry.time.length === 1 && !isNaN(Number(entry.time))) return;
    if (!entry.time || entry.time === "0") return;
  
    const hourKey = new Date(entry.time).getTime();
    if (isNaN(hourKey)) return;
  
    if (!groupedData[hourKey]) {
      groupedData[hourKey] = { time: entry.time };
    }
  
    activeMetrics.forEach((metric: string) => {
      if (!groupedData[hourKey][metric]) {
        groupedData[hourKey][metric] = 0;
      }
      groupedData[hourKey][metric] = (groupedData[hourKey][metric] as number) + (entry[metric] as number || 0);
    });
  });
  // console.log("Net_Mw_Data Before Processing:", rawChartData);
  let aggregatedData = Object.values(groupedData);
  
  
    const groupDataByInterval = (interval: number) => {
      const groupedData: { [key: string]: ChartData } = {};
    
      aggregatedData.forEach((entry: ChartData) => {
          const date = new Date(entry.time);
          let key: string = "unknown";
    
          const firstDayOfMTD = new Date();
          firstDayOfMTD.setDate(1);
          firstDayOfMTD.setHours(0, 0, 0, 0);
    
          if (interval < 1440) {
              const hour = date.getHours();
              const roundedMinutes = Math.floor(date.getMinutes() / interval) * interval;
              key = `${hour}-${roundedMinutes}`;
          } 
          else if (interval === 1440) {
              date.setHours(0, 0, 0, 0);
              if (selectedDateRange === "mtd" && date < firstDayOfMTD) {
                  return;
              }
              key = date.toISOString().split("T")[0];
          } 
          else if (interval === 10080) {
              const weekStart = new Date(date);
              weekStart.setDate(date.getDate() - date.getDay());
              weekStart.setHours(0, 0, 0, 0);
              if (selectedDateRange === "mtd" && weekStart < firstDayOfMTD) {
                  return;
              }
              key = weekStart.toISOString().split("T")[0];
          } 
          else if (interval === 43200) {
              date.setDate(1);
              date.setHours(0, 0, 0, 0);
              if (selectedDateRange === "mtd" && date < firstDayOfMTD) {
                  return;
              }
              key = `${date.getFullYear()}-${date.getMonth() + 1}`;
          }
    
          if (!groupedData[key]) {
              groupedData[key] = { time: entry.time };
          }
    
          activeMetrics.forEach((metric: string) => {
              if (!groupedData[key][metric]) {
                  groupedData[key][metric] = 0;
              }
              groupedData[key][metric] = (groupedData[key][metric] as number) + (entry[metric] as number || 0);
          });
      });
    
      return Object.values(groupedData);
  };
  

    if (useIntervalAggregation) {
      const minuteIntervals: { [key: string]: number } = {
          hour: 60,
          "15minute": 15,
          "5minute": 5,
      };
  
      const longIntervals: { [key: string]: number } = {
          day: 1440,  
          week: 10080, 
          month: 43200, 
      };
  
      const isCustomDateRange = /^\d{4}-\d{2}-\d{2}\s*-\s*\d{4}-\d{2}-\d{2}$/.test(selectedDateRange);

      if (
        ["hour", "15minute", "5minute"].includes(selectedFrequency) && 
        (
          ["minus_7d", "yesterday", "mtd", "ytd","minus_30d"].includes(selectedDateRange) || isCustomDateRange
        )
      ) {
        aggregatedData = groupDataByInterval(minuteIntervals[selectedFrequency]);
      } 
      else if (
        ["day", "week", "month"].includes(selectedFrequency) && 
        (
          ["ytd"].includes(selectedDateRange) 
        )
      ) {
        aggregatedData = groupDataByInterval(longIntervals[selectedFrequency]);
      }
      
      
  }
  
    
 const finalChartData = aggregatedData.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
 const filteredMetrics = activeMetrics.filter((metric) => metric !== "METERED_OUTPUT");

  return (
    <div className="relative">
      {isDeltaLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
          <WindmillLoading />
        </div>
      )}
      
     
            <ChartComponent
              chartData={finalChartData} 
              metrics={filteredMetrics}
              xAxis="time"
              legend={activeMetrics}
              xAxisLabel={["5minute", "15minute", "hour"].includes(selectedFrequency) ? "Time" : "Date"}
              yAxisLabel={{ value: "Mwh",  dy: 20 ,offset:-8}}
              selectedFrequency={selectedFrequency} 
              selectedDateRange={selectedDateRange}
              useIntervalAggregation={useIntervalAggregation}
              lineMetric="METERED_OUTPUT"
              lineMetricName="Meter MWh"
              lineColor="#BADA55"
              rightYAxisLabel="Meter MWh"
            />
          {/* {!isDeltaLoading && finalChartData.length === 0 && (
  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50 pointer-events-none">
    <p className="loading-text text-center text-gray-500 text-lg">
      No data available for the selected asset
    </p>
  </div>
)} */}
<ApiErrorMessage ApiError={DeltaApiError} />         
    </div>
  );
  
};

export default VolumeDeltaSetup;


